'use client';

import type { DatesSetArg, DayCellContentArg, EventClickArg, EventContentArg, MoreLinkArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { type DateClickArg } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { addDays, addMonths, isSameDay, isWithinInterval, parseISO, startOfDay, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarBirthdaysModal } from '@/app/(main)/calendar/components/CalendarBirthdaysModal';
import { renderCalendarDayCellContent } from '@/app/(main)/calendar/components/CalendarDayCellContent';
import { renderCalendarEventContent } from '@/app/(main)/calendar/components/CalendarEventContent';
import { CalendarGridSkeleton } from '@/app/(main)/calendar/components/CalendarGridSkeleton';
import { CalendarMobileToolbar } from '@/app/(main)/calendar/components/CalendarMobileToolbar';
import { CalendarNextUpPanel } from '@/app/(main)/calendar/components/CalendarNextUpPanel';
import { CalendarNextUpPanelSkeleton } from '@/app/(main)/calendar/components/CalendarNextUpPanelSkeleton';
import { OutlookEventDetailsModal } from '@/app/(main)/calendar/components/OutlookEventDetailsModal';
import { CALENDAR_EVENT_TYPES } from '@/app/(main)/calendar/consts/calendar-event-types';
import { CALENDAR_LEGEND } from '@/app/(main)/calendar/consts/calendar-panel';
import { CALENDAR_WEEK_STARTS_ON } from '@/app/(main)/calendar/consts/calendar-week';
import { useCalendarPreferences } from '@/app/(main)/calendar/hooks/useCalendarPreferences';
import { useFetchCalendarTaskById } from '@/app/(main)/calendar/hooks/useFetchCalendarTaskById';
import { useGetCalendarEventsQuery } from '@/app/(main)/calendar/hooks/useGetCalendarEventsQuery';
import {
  useUpdateCalendarTaskMutation,
  useUpdateCalendarTaskStatusMutation,
} from '@/app/(main)/calendar/hooks/useUpdateCalendarTaskMutation';
import { applyCalendarMorePopoverMaxHeight } from '@/app/(main)/calendar/utils/applyCalendarMorePopoverMaxHeight';
import { getCalendarDayCellClassNames } from '@/app/(main)/calendar/utils/calendarDayCell';
import {
  type CalendarDayHeaderArg,
  getCalendarDayHeaderClassNames,
  renderCalendarDayHeaderContent,
} from '@/app/(main)/calendar/utils/calendarDayHeader';
import { getWeekRangeForDate } from '@/app/(main)/calendar/utils/calendarMobileGrid';
import { filterEventsByVisibility } from '@/app/(main)/calendar/utils/filterEventsByVisibility';
import { formatCalendarNavTitle, formatWeekRangeLabel } from '@/app/(main)/calendar/utils/formatCalendarNavTitle';
import { getCalendarDayFromFullCalendarMarker } from '@/app/(main)/calendar/utils/getCalendarDayFromFullCalendarMarker';
import { getCalendarQueryDate } from '@/app/(main)/calendar/utils/getCalendarItemsUrl';
import { groupSummaryForDate } from '@/app/(main)/calendar/utils/groupSummaryForDate';
import {
  CALENDAR_EVENT_EXTENDED_PROP,
  getGroupedBirthdays,
  mapCalendarEventsToFullCalendar,
} from '@/app/(main)/calendar/utils/mapCalendarEventsToFullCalendar';
import { fullCalendarViewToMode, mapViewToFullCalendar } from '@/app/(main)/calendar/utils/mapCalendarViewMode';
import { hasCalendarVisibilityEventTypes } from '@/app/(main)/calendar/utils/mapCalendarVisibilityToApiEventTypes';
import {
  EditTaskModal,
  type EditTaskSaveCallbacks,
} from '@/app/(main)/client-detail/components/tabs/TasksInformation/components/EditTaskModal';
import { ViewTaskModal } from '@/app/(main)/client-detail/components/tabs/TasksInformation/components/ViewTaskModal';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { useContentDimension } from '@/context/ContentDimensionProvider';
import { useModalState } from '@/hooks/useModalState';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import type { CalendarEvent, CalendarViewMode } from '@/types/calendarTypes';
import type { ClientTaskData, UpdateClientTaskRequest } from '@/types/clientTasksApiTypes';
import { cn } from '@/utils/сn';
import './calendar-panel.css';
import { calendarPanelLayoutStyles, calendarPanelNavStyles, calendarPanelRootStyles } from './styles';
import type { CalendarPanelProps } from './types';
import ChevronRightIcon from '/public/icons/chevron-right-pagination.svg';

export function CalendarPanel({ variant }: CalendarPanelProps) {
  const fetchCalendarTaskById = useFetchCalendarTaskById();
  const { isMobile } = useContentDimension();
  const isMobileFullPage = variant === 'full' && isMobile;
  const { view: preferredView, visibility, setView, setVisibility, isLoadingInitial } = useCalendarPreferences();

  const view = variant === 'widget' && isMobile ? 'day' : preferredView;

  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [weekStart, setWeekStart] = useState(() => startOfDay(new Date()));
  const [weekEnd, setWeekEnd] = useState(() => startOfDay(new Date()));
  const [titleAnchor, setTitleAnchor] = useState(() => startOfDay(new Date()));
  const [queryDate, setQueryDate] = useState<string | null>(null);

  const calendarRef = useRef<InstanceType<typeof FullCalendar>>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const initialCalendarDateRef = useRef<Date | null>(null);
  if (initialCalendarDateRef.current === null) {
    initialCalendarDateRef.current = new Date();
  }

  const hasEverLoadedEventsRef = useRef(false);

  const {
    data: fetchedEvents,
    isPending: isEventsPending,
    isFetching: isEventsFetching,
    isPlaceholderData: isEventsPlaceholderData,
  } = useGetCalendarEventsQuery({
    date: queryDate ?? '',
    view,
    visibility,
  });

  const isEventsTransitioning = isEventsFetching && isEventsPlaceholderData;

  if (fetchedEvents !== undefined && !isEventsTransitioning) {
    hasEverLoadedEventsRef.current = true;
  }

  const events =
    hasCalendarVisibilityEventTypes(visibility) && fetchedEvents && !isEventsTransitioning ? fetchedEvents : [];

  const isSectionLoading = isLoadingInitial || (isEventsPending && !hasEverLoadedEventsRef.current);

  const syncCalendarSize = useCallback(() => {
    calendarRef.current?.getApi().updateSize();
  }, []);

  useResizeObserver({
    target: calendarContainerRef,
    onResize: syncCalendarSize,
    enabled: !isSectionLoading,
  });

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    const targetView = mapViewToFullCalendar(view);
    if (calendarApi.view.type !== targetView) {
      calendarApi.changeView(targetView);
    }
  }, [view, isSectionLoading]);

  const filteredForSummary = useMemo(() => filterEventsByVisibility(events, visibility), [events, visibility]);
  const summary = useMemo(
    () => groupSummaryForDate(filteredForSummary, selectedDate),
    [filteredForSummary, selectedDate]
  );

  const fcEvents = useMemo(() => {
    const calendarEvents =
      view === 'month' ? events.filter((event) => event.type !== CALENDAR_EVENT_TYPES.BIRTHDAY) : events;

    return mapCalendarEventsToFullCalendar(calendarEvents, visibility);
  }, [events, visibility, view]);

  const navTitle = useMemo(
    () => formatCalendarNavTitle(view, titleAnchor, weekStart, weekEnd),
    [view, titleAnchor, weekStart, weekEnd]
  );

  const mobileNavTitle = useMemo(() => {
    if (view === 'day') {
      const { weekStart: mobileWeekStart, weekEnd: mobileWeekEnd } = getWeekRangeForDate(selectedDate);
      return formatWeekRangeLabel(mobileWeekStart, mobileWeekEnd);
    }
    return formatCalendarNavTitle(view, titleAnchor, weekStart, weekEnd);
  }, [view, selectedDate, titleAnchor, weekStart, weekEnd]);

  const { isOpen: isOutlookOpen, open: openOutlook, close: closeOutlook } = useModalState();
  const [outlookEvent, setOutlookEvent] = useState<CalendarEvent | null>(null);

  const { isOpen: isViewTaskOpen, open: openViewTask, close: closeViewTask } = useModalState();
  const { isOpen: isEditTaskOpen, open: openEditTask, close: closeEditTask } = useModalState();
  const [activeTask, setActiveTask] = useState<ClientTaskData | null>(null);

  const updateTask = useUpdateCalendarTaskMutation();
  const toggleTaskStatus = useUpdateCalendarTaskStatusMutation();

  const birthdayEvents = useMemo(
    () => filteredForSummary.filter((event) => event.type === CALENDAR_EVENT_TYPES.BIRTHDAY),
    [filteredForSummary]
  );

  const { isOpen: isBirthdaysOpen, open: openBirthdays, close: closeBirthdays } = useModalState();
  const [birthdayModalDate, setBirthdayModalDate] = useState<Date | null>(null);
  const [birthdayModalEvents, setBirthdayModalEvents] = useState<CalendarEvent[]>([]);

  const goToToday = useCallback(() => {
    const today = startOfDay(new Date());
    setSelectedDate(today);
    setTitleAnchor(today);
    calendarRef.current?.getApi().gotoDate(today);
  }, []);

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    setQueryDate(getCalendarQueryDate(arg));
    const mode = fullCalendarViewToMode(arg.view.type);
    const periodStart = startOfDay(arg.view.currentStart);
    const visibleStart = startOfDay(arg.start);
    const visibleEnd = startOfDay(subDays(arg.end, 1));
    setWeekStart(visibleStart);
    setWeekEnd(visibleEnd);

    if (mode === 'day') {
      setTitleAnchor(visibleStart);
      setSelectedDate(visibleStart);
      return;
    }
    if (mode === 'week') {
      setTitleAnchor(visibleStart);
      setSelectedDate((prev) => {
        const p = startOfDay(prev);
        if (isWithinInterval(p, { start: visibleStart, end: visibleEnd })) return p;
        return visibleStart;
      });
      return;
    }

    const monthEnd = startOfDay(subDays(addMonths(periodStart, 1), 1));
    setTitleAnchor(periodStart);
    setSelectedDate((prev) => {
      const p = startOfDay(prev);
      if (isWithinInterval(p, { start: periodStart, end: monthEnd })) return p;
      return periodStart;
    });
  }, []);

  const handleDateClick = useCallback((arg: DateClickArg) => {
    if (arg.dayEl.classList.contains('fc-day-other')) {
      return;
    }

    const d = startOfDay(arg.date);
    setSelectedDate((prev) => (isSameDay(startOfDay(prev), d) ? prev : d));
  }, []);

  const handleWeekDayHeaderClick = useCallback((date: Date) => {
    const d = startOfDay(date);
    setSelectedDate((prev) => (isSameDay(startOfDay(prev), d) ? prev : d));
  }, []);

  const handleTaskClick = useCallback(
    async (taskId: string) => {
      try {
        const task = await fetchCalendarTaskById(taskId);
        if (task) {
          setActiveTask(task);
          openViewTask();
        }
      } catch {}
    },
    [fetchCalendarTaskById, openViewTask]
  );

  const handleOpenBirthdaysModal = useCallback(
    (date: Date, birthdays: CalendarEvent[]) => {
      setBirthdayModalDate(startOfDay(date));
      setBirthdayModalEvents(birthdays);
      openBirthdays();
    },
    [openBirthdays]
  );

  const handleCloseBirthdaysModal = useCallback(() => {
    closeBirthdays();
    setBirthdayModalDate(null);
    setBirthdayModalEvents([]);
  }, [closeBirthdays]);

  const handleEventClick = useCallback(
    async (info: EventClickArg) => {
      const clickedDeadlineCheckbox =
        info.jsEvent.target instanceof Element && info.jsEvent.target.closest('.sf-calendar-event__deadline-checkbox');

      if (clickedDeadlineCheckbox) {
        return;
      }

      const ev = info.event.extendedProps[CALENDAR_EVENT_EXTENDED_PROP] as CalendarEvent | undefined;
      if (!ev) return;
      if (ev.type === CALENDAR_EVENT_TYPES.BIRTHDAY) {
        info.jsEvent.preventDefault();
        handleOpenBirthdaysModal(parseISO(ev.start), getGroupedBirthdays(info.event, ev));
        return;
      }
      if (ev.type === CALENDAR_EVENT_TYPES.TASK_DEADLINE || ev.type === CALENDAR_EVENT_TYPES.TASK_REMINDER) {
        if (!ev.taskId) return;
        await handleTaskClick(ev.taskId);
        return;
      }
      setOutlookEvent(ev);
      openOutlook();
    },
    [handleOpenBirthdaysModal, handleTaskClick, openOutlook]
  );

  const handleToggleTaskComplete = useCallback(
    (taskId: string, completed: boolean) => {
      toggleTaskStatus.mutate({ taskId, completed });
    },
    [toggleTaskStatus]
  );

  const eventContent = useCallback(
    (arg: EventContentArg) =>
      renderCalendarEventContent(arg, { isMobile, onToggleTaskComplete: handleToggleTaskComplete }),
    [handleToggleTaskComplete, isMobile]
  );

  const handleMoreLinkClick = useCallback((info: MoreLinkArg) => {
    const day = getCalendarDayFromFullCalendarMarker(info.date);
    setSelectedDate((prev) => (isSameDay(prev, day) ? prev : day));

    if (calendarContainerRef.current) {
      applyCalendarMorePopoverMaxHeight(calendarContainerRef.current);
    }

    return 'popover' as const;
  }, []);

  const handleViewSegment = useCallback(
    (next: CalendarViewMode) => {
      const previousView = view;

      const applyCalendarView = (mode: CalendarViewMode) => {
        const api = calendarRef.current?.getApi();
        if (!api) return;
        api.changeView(mapViewToFullCalendar(mode));
        if (mode === 'day') {
          goToToday();
        }
      };

      applyCalendarView(next);
      setView(next, {
        onError: () => applyCalendarView(previousView),
      });
    },
    [goToToday, setView, view]
  );

  const handleCalendarPrev = () => {
    calendarRef.current?.getApi().prev();
  };

  const handleCalendarNext = () => {
    calendarRef.current?.getApi().next();
  };

  const handleMobileCalendarPrev = useCallback(() => {
    if (view === 'day') {
      const { weekStart } = getWeekRangeForDate(selectedDate);
      const nextDate = startOfDay(subDays(weekStart, 7));
      setSelectedDate(nextDate);
      calendarRef.current?.getApi().gotoDate(nextDate);
      return;
    }
    calendarRef.current?.getApi().prev();
  }, [selectedDate, view]);

  const handleMobileCalendarNext = useCallback(() => {
    if (view === 'day') {
      const { weekStart } = getWeekRangeForDate(selectedDate);
      const nextDate = startOfDay(addDays(weekStart, 7));
      setSelectedDate(nextDate);
      calendarRef.current?.getApi().gotoDate(nextDate);
      return;
    }
    calendarRef.current?.getApi().next();
  }, [selectedDate, view]);

  const handleMobileWeekDateSelect = useCallback((date: Date) => {
    const nextDate = startOfDay(date);
    setSelectedDate(nextDate);
    calendarRef.current?.getApi().gotoDate(nextDate);
  }, []);

  const handleSaveTask = (taskId: number, data: UpdateClientTaskRequest, callbacks?: EditTaskSaveCallbacks) => {
    updateTask.mutate(
      { taskId, data },
      {
        onSuccess: () => {
          closeEditTask();
          setActiveTask(null);
          callbacks?.onSuccess?.();
        },
        onError: (error) => {
          callbacks?.onError?.(error);
        },
      }
    );
  };

  const handleCloseOutlook = () => {
    setOutlookEvent(null);
    closeOutlook();
  };

  const dayHeaderClassNames = useCallback(
    (arg: CalendarDayHeaderArg) => getCalendarDayHeaderClassNames(arg, selectedDate),
    [selectedDate]
  );

  const dayHeaderContent = useCallback(
    (arg: CalendarDayHeaderArg) => {
      if (isMobileFullPage && arg.view.type === 'timeGridDay') {
        return null;
      }

      return renderCalendarDayHeaderContent(arg, {
        selectedDate,
        onWeekDayHeaderClick: handleWeekDayHeaderClick,
      });
    },
    [handleWeekDayHeaderClick, isMobileFullPage, selectedDate]
  );

  const dayCellClassNames = useCallback(
    (arg: DayCellContentArg) => getCalendarDayCellClassNames(arg, selectedDate),
    [selectedDate]
  );

  const dayCellContent = useCallback(
    (arg: DayCellContentArg) =>
      renderCalendarDayCellContent(arg, {
        birthdayEvents,
        isMobile,
        onOpenBirthdaysModal: handleOpenBirthdaysModal,
      }),
    [birthdayEvents, handleOpenBirthdaysModal, isMobile]
  );

  const showNextUp = variant === 'widget' || (variant === 'full' && !isMobile);

  const fullCalendarSizing = useMemo(() => {
    if (!isMobileFullPage) return { height: '100%' as const };
    if (view === 'day') return { height: 'auto' as const };
    return { aspectRatio: 0.9 };
  }, [isMobileFullPage, view]);

  return (
    <div
      className={cn(calendarPanelRootStyles({ variant }), {
        'sf-calendar--mobile-full': isMobileFullPage,
      })}
    >
      {variant === 'full' ? (
        <CalendarMobileToolbar
          view={view}
          navTitle={mobileNavTitle}
          selectedDate={selectedDate}
          visibility={visibility}
          onPrevious={handleMobileCalendarPrev}
          onNext={handleMobileCalendarNext}
          onViewSegment={handleViewSegment}
          onVisibilityChange={setVisibility}
          onWeekDateSelect={handleMobileWeekDateSelect}
        />
      ) : null}
      <div
        className={cn('flex items-center justify-between gap-3', {
          'max-md:hidden': variant === 'full',
        })}
      >
        <div className={calendarPanelNavStyles({ variant })}>
          <IconButton
            icon={<ChevronRightIcon className="w-4 h-4 min-w-4 min-h-4 text-blue-300 rotate-180" />}
            aria-label="Previous"
            onClick={handleCalendarPrev}
          >
            ‹
          </IconButton>
          <h3
            title={navTitle}
            className="light text-blue-300 truncate text-center min-w-[12rem] max-w-[min(100%,28rem)]"
          >
            {navTitle}
          </h3>
          <IconButton
            icon={<ChevronRightIcon className="w-4 h-4 min-w-4 min-h-4 text-blue-300" />}
            aria-label="Next"
            onClick={handleCalendarNext}
          >
            ›
          </IconButton>
          {variant === 'full' && (
            <BaseButton intent="ghost" size="medium" onClick={goToToday}>
              Today
            </BaseButton>
          )}
        </div>
        <div className="flex items-center xl:gap-6 gap-3">
          <div className="hidden xl:flex flex-wrap items-center justify-center gap-4 text-tp-body-small text-grey-700">
            {CALENDAR_LEGEND.map((item) => (
              <span key={item.label} className="inline-flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full mb-1 ${item.className}`} />
                <span className="truncate">{item.label}</span>
              </span>
            ))}
          </div>

          <div className="md:flex hidden items-center gap-2">
            <div className="inline-flex rounded-[12px] bg-grey-100 p-1.5">
              {(['day', 'week', 'month'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`rounded-[6px] px-[18px] py-2 text-body-small capitalize transition ${
                    view === v ? 'bg-white text-blue-300 shadow-card' : 'text-grey-600 cursor-pointer'
                  }`}
                  onClick={() => handleViewSegment(v)}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={calendarPanelLayoutStyles({ variant })}>
        {showNextUp ? (
          isSectionLoading ? (
            <CalendarNextUpPanelSkeleton showFooterCta={variant === 'widget'} />
          ) : (
            <CalendarNextUpPanel
              isLoading={isEventsTransitioning}
              onPrevious={handleCalendarPrev}
              onNext={handleCalendarNext}
              selectedDate={selectedDate}
              summary={summary}
              visibility={visibility}
              view={view}
              titleAnchor={titleAnchor}
              weekStart={weekStart}
              weekEnd={weekEnd}
              onVisibilityChange={setVisibility}
              onToggleTaskComplete={handleToggleTaskComplete}
              onTaskClick={handleTaskClick}
              showFooterCta={variant === 'widget'}
            />
          )
        ) : null}

        <div
          ref={calendarContainerRef}
          className={cn('min-w-0 w-full flex-col', {
            'md:flex hidden': variant === 'widget',
            'h-auto': isMobileFullPage,
            'min-h-0 h-full': !isMobileFullPage,
            flex: variant === 'full',
          })}
        >
          {isSectionLoading ? (
            <CalendarGridSkeleton variant={variant} />
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={mapViewToFullCalendar(view)}
              initialDate={initialCalendarDateRef.current}
              headerToolbar={false}
              {...fullCalendarSizing}
              editable={false}
              selectable={false}
              dayMaxEvents={3}
              moreLinkClick={handleMoreLinkClick}
              nowIndicator
              firstDay={CALENDAR_WEEK_STARTS_ON}
              slotMinTime="07:00:00"
              slotMaxTime="21:00:00"
              slotDuration="01:00:00"
              slotLabelInterval="01:00:00"
              slotEventOverlap={false}
              allDayText="ALL DAY"
              allDaySlot
              events={fcEvents}
              datesSet={handleDatesSet}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              eventContent={eventContent}
              dayHeaderClassNames={dayHeaderClassNames}
              dayHeaderContent={dayHeaderContent}
              dayCellClassNames={dayCellClassNames}
              dayCellContent={dayCellContent}
            />
          )}
        </div>
      </div>

      <OutlookEventDetailsModal event={outlookEvent} isOpen={isOutlookOpen} onClose={handleCloseOutlook} />

      <CalendarBirthdaysModal
        date={birthdayModalDate}
        birthdays={birthdayModalEvents}
        isOpen={isBirthdaysOpen}
        onClose={handleCloseBirthdaysModal}
      />

      <ViewTaskModal
        task={activeTask}
        isOpen={isViewTaskOpen}
        onClose={() => {
          closeViewTask();
          setActiveTask(null);
        }}
        onEdit={(task) => {
          closeViewTask();
          setActiveTask(task);
          openEditTask();
        }}
      />

      <EditTaskModal
        task={activeTask}
        isOpen={isEditTaskOpen}
        onClose={() => {
          closeEditTask();
          setActiveTask(null);
        }}
        onSave={handleSaveTask}
        isPending={updateTask.isPending}
        disableFileUpload
      />
    </div>
  );
}
